/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import React, { useEffect } from 'react';
import {
  Button,
  LoadingElement,
  LoadingSpinner,
  // @ts-expect-error TODO: @checkout/react-components
} from '@checkout/react-components';
// TODO: Fix checkout-apple-pay bundling
import '@paypalcorp/checkout-apple-pay/dist/esm/styles.css';
import {
  ApplePayButtonSimple,
  createSession,
  useApplePayGuestDisclosure,
} from '@paypalcorp/checkout-apple-pay';

// Internal libraries
import {
  CI,
  features,
  getContent,
  getGlobals,
  // @ts-expect-error TODO: getLazyLoaderRenderFunction
  getLazyLoaderRenderFunction,
  getLocale,
  getToken,
  // @ts-expect-error TODO:  lazyLoader
  lazyLoader,
  logger,
  redirectToLogIn,
  useNavigateToReceiptPage,
} from '@/lib';
import { useWalletOptions } from '@/lib/reactHooks/useWalletOptions';
import { WalletsList } from './WalletsList/WalletsList';
import {
  ApplePayGuestDisclaimerMessage,
  BaslButtonContainer,
  BaslHaveAnAccountText,
  DisclaimerMessageContainer,
  OrDivider,
  OrText,
  WalletsListContainer,
} from './StyledComponents';
import { Link, StyledText } from '../Common';
import { useStoreActions, useStoreState } from '@/store';
import {
  BaslApplePayLinkStyles,
  logInButtonStyleOverrides,
  nonBaslLinkStyles,
} from './styles';
import { streamlineGuestTermsEmbeddedLinks } from '@/components/OnboardTerms/StreamlineOnboardTerms/streamlineTermsEmbeddedLinks';
import {
  applePayPaymentRequest,
  merchantAddressConfigRequired,
} from '@/components/Landing/applepay';
import {
  logBillingApplePayButtonClicked,
  logBillingApplePayButtonShown,
  logBillingWalletOptionClicked,
} from './fpti';
import { APPLE_PAY_BILLING_BUTTON_METRICS } from './constants';

import {
  getCachedCheckoutSession,
  getCachedShippingAndTax,
  handleBuildApplePayMerchantConfig,
} from '@/components/Billing/utils';
import type { MerchantAddressConfiguration } from '@/components/Billing/types';
import type { CountryCodes } from '@/generated/graphql';

const BaslLogInLink = ({
  logInElement,
}: {
  logInElement: JSX.Element;
}): React.ReactElement => (
  <div data-testid="basl-login-link-container">
    <BaslButtonContainer>
      <BaslHaveAnAccountText>
        {getContent('billing.haveAnAccount')}
      </BaslHaveAnAccountText>

      {logInElement}
    </BaslButtonContainer>

    <BaslPpwWallets />

    <OrDivider>
      <OrText>{getContent('base.or')}</OrText>
    </OrDivider>
  </div>
);

const NonBaslLogInLink = ({
  logInElement,
}: {
  logInElement: JSX.Element;
}): React.ReactElement => (
  <StyledText css={nonBaslLinkStyles} data-testid="have-an-account-text">
    {getContent('billing.haveAnAccount', { logInLink: logInElement })}
  </StyledText>
);

const LogInLink = ({
  children,
  linkType = 'link',
}: {
  children: React.ReactChildren | React.ReactNode[] | string;
  linkType: 'button' | 'link';
}): JSX.Element => {
  const isButton = linkType === 'button';
  const Component = isButton ? Button : Link;
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const toggleIsPageLoading = useStoreActions(({ ui }) => {
    return ui.toggleIsPageLoading;
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isFormSubmitting) {
      toggleIsPageLoading(true);
      redirectToLogIn();
    }
  };
  const props = {
    children,
    onClick: handleClick,
    ...(isButton && {
      styleOverrides: logInButtonStyleOverrides,
    }),
    ...(!isButton && {
      href: '#',
      testId: 'text-log-in-link',
      text: children,
    }),
  };

  return <Component {...props} />;
};

const BillingWalletsList = ({
  locale,
  buyerCountry,
}: {
  locale: string;
  buyerCountry: CountryCodes;
}): JSX.Element | null => {
  const token = getToken();
  const { isLoading, checkoutOptions, uiContent, ppwLogoUrl } =
    useWalletOptions({
      buyerCountry,
      locale,
      token,
    });

  return (
    <WalletsList
      isLoading={isLoading}
      checkoutOptions={checkoutOptions}
      connectedByLabel={uiContent?.connectedByLabel ?? ''}
      ppwWalletsLabel={uiContent?.ppwWalletsLabel ?? ''}
      ppwWalletsDescription={uiContent?.ppwWalletsDescription ?? ''}
      ppwLogoUrl={ppwLogoUrl ?? ''}
      onWalletClick={logBillingWalletOptionClicked}
    />
  );
};

// PPW wallet eligibility is a standalone feature, unrelated to Apple Pay
// eligibility. Kept as its own component so BaslLogInLink/BaslApplePayLogInLink
// don't each need to know about (or duplicate) this feature flag.
const BaslPpwWallets = (): React.ReactElement | null => {
  const isBalpPpwWalletEligible = features.isActive('isBalpPpwWalletEligible');
  const { locale, country } = getLocale();

  if (!isBalpPpwWalletEligible) {
    return null;
  }

  return (
    <WalletsListContainer>
      <BillingWalletsList locale={locale} buyerCountry={country} />
    </WalletsListContainer>
  );
};

const BaslHaveAnAccountLogIn = ({
  isBaslApplePay = false,
}: {
  isBaslApplePay: boolean;
}): React.ReactElement => {
  const Component = isBaslApplePay ? BaslApplePayLogInLink : BaslLogInLink;
  const LogIn = (
    <LogInLink linkType={isBaslApplePay ? 'link' : 'button'}>
      {getContent('billing.logIn')}
    </LogInLink>
  );
  return <Component logInElement={LogIn} />;
};

const HaveAnAccountLogIn = (): React.ReactElement => {
  const LogIn = (
    <LogInLink linkType={'link'}>{getContent('billing.logIn')}</LogInLink>
  );
  return <NonBaslLogInLink logInElement={LogIn} />;
};

// keep the CPA disclaimer message component for future testing
const CPADisclaimerMessage = (): React.ReactElement => {
  return (
    <DisclaimerMessageContainer>
      {getContent('disclaimer.cardPreAuthorization.cartAbandoment.TREATMENT_1')}
    </DisclaimerMessageContainer>
  );
};

const LazyInventorySoldOutInterstitial = lazyLoader({
  loader: () =>
    import('@/components/Interstitials/InventorySoldOutInterstitial'),
  loading: LoadingElement,
  name: 'inventory_sold_out',
  render: getLazyLoaderRenderFunction({
    componentName: 'InventorySoldOutInterstitial',
  }),
});

const LazyVenmoMessage = lazyLoader({
  loader: () => import('../VenmoMessage'),
  loading: LoadingElement,
  name: 'VenmoMessage',
  render: getLazyLoaderRenderFunction({ componentName: 'VenmoMessage' }),
});

const LazyConsumerAddBankForm = lazyLoader({
  loader: () => import('../ConsumerAddBankForm'),
  loading: () => <LoadingSpinner />,
  name: 'consumer_add_bank_form',
  render(loaded: unknown, props: unknown) {
    // @ts-expect-error
    const Component = loaded.ConsumerAddBankForm;
    return <Component />;
  },
});

const LazyCartRender = lazyLoader({
  loader: () => import('../Header/CartDropdown/CartRender'),
  loading: LoadingElement,
  name: 'CartRender',
  render: getLazyLoaderRenderFunction({ componentName: 'CartRender' }),
});

const ApplePayButtonWrapper = ({
  showDividerOrText = false,
}: {
  showDividerOrText?: boolean;
}): JSX.Element => {
  const { country } = getLocale();
  const showApplePayGuestDisclosure = useApplePayGuestDisclosure(country);
  const navigateToReceiptPage = useNavigateToReceiptPage();
  const { initialGqlData } = getGlobals() || {};

  const isSntSupported = features.isActive('isSntSupported');

  const handleClick = () => {
    CI.start(CI.Event.APPLE_PAY_BASL_RENDER);
    try {
      let merchantAddressConfig: MerchantAddressConfiguration | undefined = {
        merchantShippingConfig: {
          charges: [],
          shippingOptionsSource: '',
        },
        paymentRequest: {
          lineItems: null,
          shippingContact: null,
          shippingMethods: null,
          total: null,
        },
      };
      const cart = initialGqlData?.checkoutSession?.cart;

      const merchantDidPassAddress = merchantAddressConfigRequired(cart);

      // Only build a new merchant config if the merchant passed address params.
      if (merchantDidPassAddress) {
        // The Billing page always calls shipping and taxes. We are writing the
        // result to the apollo cache. Looking at the cache prevents a duplicate
        // set of calls being made.

        const cachedCheckoutSession = getCachedCheckoutSession();

        // Only check in the cache when SnT is supported!
        const shippingAndTaxesResponse = isSntSupported
          ? getCachedShippingAndTax()
          : undefined;

        merchantAddressConfig = handleBuildApplePayMerchantConfig({
          cart: cachedCheckoutSession?.checkoutSession?.cart,
          merchant: initialGqlData?.checkoutSession?.merchant,
          shippingAndTaxes: shippingAndTaxesResponse?.calculateShippingAndTaxes,
        });
      }

      logBillingApplePayButtonClicked();
      createSession(
        applePayPaymentRequest(
          initialGqlData?.checkoutSession,
          navigateToReceiptPage,
          merchantAddressConfig,
        ),
      ).begin();
      logger.metric(APPLE_PAY_BILLING_BUTTON_METRICS.CLICK_COMPLETE);
    } catch (error) {
      logger.metric(APPLE_PAY_BILLING_BUTTON_METRICS.CLICK_ERROR);
      logger.error('BILLING_PAY_WITH_OTHER_FUNDING_SOURCE_CLICKED.ERROR', {
        error_message: error instanceof Error ? error.message : String(error),
      });

      CI.endWith(CI.Event.APPLE_PAY_BASL_RENDER, CI.Status.FCI);
    }
  };

  useEffect(() => {
    logBillingApplePayButtonShown();
  }, []);

  const showDomesticGuestUADisclaimer = features.isActive(
    'shouldShowDomesticUserAgreementAcceptance',
  );
  const guestTermKey = showDomesticGuestUADisclaimer
    ? 'simplifiedSectionHeaders.guest.domesticTermsWithLinks'
    : 'simplifiedSectionHeaders.guest.termsWithLinks';

  return (
    <div data-testid="apple-pay-button-container">
      <ApplePayButtonSimple
        onClick={handleClick}
        buttonStyle={'white'}
        style={{ color: 'black', marginBottom: '24px' }}
      />
      {showApplePayGuestDisclosure && (
        <ApplePayGuestDisclaimerMessage>
          {getContent(guestTermKey, streamlineGuestTermsEmbeddedLinks())}
        </ApplePayGuestDisclaimerMessage>
      )}
      <OrDivider>
        {showDividerOrText && <OrText>{getContent('base.or')}</OrText>}
      </OrDivider>
    </div>
  );
};

const BaslApplePayLogInLink = ({
  logInElement,
}: {
  logInElement: JSX.Element;
}): React.ReactElement => (
  <div data-testid="basl-apple-pay-login-link-container">
    <StyledText css={BaslApplePayLinkStyles} data-testid="have-an-account-text">
      {getContent('billing.haveAnAccount', { logInLink: logInElement })}
    </StyledText>

    <BaslPpwWallets />

    <OrDivider>
      <OrText>{getContent('base.or')}</OrText>
    </OrDivider>
  </div>
);

export {
  ApplePayButtonWrapper,
  BaslApplePayLogInLink,
  BaslHaveAnAccountLogIn,
  BillingWalletsList,
  CPADisclaimerMessage,
  HaveAnAccountLogIn,
  LazyCartRender,
  LazyConsumerAddBankForm,
  LazyInventorySoldOutInterstitial,
  LazyVenmoMessage,
  LogInLink,
};
